import completeSsoActionService from 'services/api/complete-sso-action'
import { DoMoreProps, DoMoreMessageType } from 'util/interfaces'
import globalConfig from 'init/global.config'
import msg from './msg'
import { RestError } from './api-service'
import submissionStore, { LaunchData, QaLaunchData, SellerRatingLaunchData, Source, SubmissionPage }
  from 'store/submission-store'
import { hostsConfig, turnToConfig } from 'init/config'
import submissionLoader from 'widgets/submission/submission-loader'

type LaunchType = 'qa' | 'rev' | 'sr'

export interface SsoContext {
  rating?: number
  sku?: string
  submissionToken?: string
  action?: string
  authSetting?: string
  source?: Source
  page?: SubmissionPage // used when launch from solicitation email
  questionId?: number
  // used when launch from solicitation email
  launchData?: LaunchData | QaLaunchData | SellerRatingLaunchData
  launchType?: LaunchType
}

function encodeSsoContext(ssoContext: SsoContext): string {
  return btoa(JSON.stringify(ssoContext))
}

function processSsoRegDone(params: { context: string; userDataToken: string }): void {
  const ssoContextData = parseSsoContext(params.context)
  if (params.userDataToken === null || params.userDataToken === '') {
    if (ssoContextData.action === 'QUESTION_CREATE' && globalConfig.isAllowAnon()) {
      import(/* webpackChunkName: 'standalone-popover' */ 'widgets/standalone-popover/standalone-popover-loader').then((popoverLoader) => {
        popoverLoader.default.load({ message: msg('ia.reg.skipped'), autoClose: true })
      })
    }
    return
  }
  completeSsoActionService.completeSsoAction({
    sku: ssoContextData.sku
  }, {
    submissionToken: ssoContextData.submissionToken,
    action: ssoContextData.action,
    userDataToken: params.userDataToken
  }).then((ssoResponse) => {
    // Review submission - purchase required case
    if (ssoContextData.action === 'REVIEW_CREATE' && globalConfig.isPurchaseReq()) {
      const launchData = {
        startAction: 'review',
        fromSso: true,
        source: ssoContextData.source,
        ssoToken: ssoResponse.token,
        starSelected: typeof ssoContextData.rating === 'number' ? ssoContextData.rating : undefined
      } as LaunchData
      submissionStore.startRevFlow(
        ssoContextData.sku,
        hostsConfig.siteKey,
        { page: 'review-form', props: {} } as SubmissionPage,
        launchData
      )
      submissionLoader.load()
    } else {
      let messageType = 'review' as DoMoreMessageType
      let eventHandlerName = 'onReviewSubmit'
      switch (ssoContextData.action) {
        case 'QUESTION_CREATE':
          messageType = globalConfig.isAllowAnon() ? 'questionAnonSso' : 'question'
          // already called when question was submitted if anonymous is allowed
          eventHandlerName = !globalConfig.isAllowAnon() ? 'onQuestionSubmit' : null
          break
        case 'ANSWER_CREATE':
          messageType = 'answer'
          eventHandlerName = 'onAnswerSubmit'
          break
        case 'REPLY_CREATE':
          messageType = 'reply'
          eventHandlerName = null
      }
      submissionStore.launchData = { ssoToken: ssoResponse.token }
      submissionStore.currentPage = {
        page: 'do-more',
        props: {
          msgType: messageType,
          questionId: ssoContextData.questionId || null
        } as DoMoreProps
      } as SubmissionPage
      submissionLoader.load()
      const eventHandlers = window.turnToConfig.eventHandlers || {}
      // if client provided an the appropriate event handler (e.g. onReviewSubmit)
      if (eventHandlerName && eventHandlers[eventHandlerName] && ssoResponse.ugcId) {
        eventHandlers[eventHandlerName]({ id: ssoResponse.ugcId })
      }
    }
    // stops spinner on write review button(s)
    submissionStore.isLaunchingWriteReview = false
  }).catch((e: RestError) => {
    const errorMsgKey = e.errorCode === 'item-already-reviewed'
      ? 'revForm.alreadyReviewed.message'
      : e.errorCode === 'turnto-offline' ? 'offline.error' : 'unexpected.error'
    import(/* webpackChunkName: 'standalone-popover' */ 'widgets/standalone-popover/standalone-popover-loader').then((popoverLoader) => {
      popoverLoader.default.load({ message: msg(errorMsgKey), type: 'error', autoClose: true })
    })
  })
}

function ssoCreateReview(sku: string, source: Source, submissionToken = null, rating = null): void {
  switch (globalConfig.authSettings.review) {
    case 1:
      ssoUserData(sku, 'REVIEW_CREATE', 'REGISTRATION_REQUIRED', submissionToken, source, rating)
      break
    case 3:
      ssoUserData(sku, 'REVIEW_CREATE', 'CONFIRMATION_REQUIRED', submissionToken, source, rating)
      break
    case 4:
      ssoUserData(sku, 'REVIEW_CREATE', 'PURCHASE_REQUIRED', submissionToken, source, rating)
  }
}

function ssoCreateQuestion(sku: string, submissionToken = null): void {
  switch (globalConfig.authSettings.question) {
    case 1:
      ssoUserData(sku, 'QUESTION_CREATE', 'ANONYMOUS', submissionToken)
      break
    case 2:
      ssoUserData(sku, 'QUESTION_CREATE', 'REGISTRATION_REQUIRED', submissionToken)
      break
    case 3:
      ssoUserData(sku, 'QUESTION_CREATE', 'CONFIRMATION_REQUIRED', submissionToken)
  }
}

function ssoCreateAnswer(sku: string, submissionToken: string, questionId = null): void {
  switch (globalConfig.authSettings.answerReply) {
    case 1:
      ssoUserData(
        sku, 'ANSWER_CREATE', 'REGISTRATION_REQUIRED', submissionToken, null, null, questionId
      )
      break
    case 2:
      ssoUserData(
        sku, 'ANSWER_CREATE', 'CONFIRMATION_REQUIRED', submissionToken, null, null, questionId
      )
  }
}

function ssoCreateReply(sku: string, submissionToken: string): void {
  switch (globalConfig.authSettings.answerReply) {
    case 1:
      ssoUserData(sku, 'REPLY_CREATE', 'REGISTRATION_REQUIRED', submissionToken)
      break
    case 2:
      ssoUserData(sku, 'REPLY_CREATE', 'CONFIRMATION_REQUIRED', submissionToken)
  }
}

function ssoGetLoggedInData(
  sku: string,
  page: SubmissionPage,
  launchData: LaunchData | QaLaunchData | SellerRatingLaunchData,
  launchType: LaunchType
): void {
  const ssoContext = {} as SsoContext
  ssoContext.sku = sku
  ssoContext.page = page
  ssoContext.launchData = launchData
  ssoContext.launchType = launchType
  const encodedSsoContext = encodeSsoContext(ssoContext) as string
  turnToConfig.sso.loggedInDataFn(encodedSsoContext)
}

function ssoGetLoggedInDataForReview(sku: string, source: number, rating = null): void {
  ssoGetLoggedInData(
    sku,
    { page: 'review-form', props: {} } as SubmissionPage,
    {
      startAction: 'review',
      source: typeof source === 'number' ? source : undefined,
      starSelected: typeof rating === 'number' ? rating : undefined
    },
    'rev'
  )
}

function processLoggedInDataDone(params: { context: string; loggedInData: string }): void {
  const ssoContextData = parseSsoContext(params.context)
  if (params.loggedInData) {
    ssoContextData.launchData.ssoProfileToken = params.loggedInData
  }
  if (ssoContextData.launchType === 'qa') {
    submissionStore.startQaFlow(
      ssoContextData.sku,
      hostsConfig.siteKey,
      ssoContextData.page,
      ssoContextData.launchData as QaLaunchData
    )
  } else if (ssoContextData.launchType === 'sr') {
    submissionStore.startSellerRatingFlow(
      hostsConfig.siteKey,
      ssoContextData.page,
      ssoContextData.launchData as SellerRatingLaunchData
    )
  } else { // only other flow
    submissionStore.startRevFlow(
      ssoContextData.sku,
      hostsConfig.siteKey,
      ssoContextData.page,
      ssoContextData.launchData
    )
  }
  submissionLoader.load()
  // stops spinner on write review button(s)
  submissionStore.isLaunchingWriteReview = false
}

function logout(): void {
  if (turnToConfig.sso.logout) {
    turnToConfig.sso.logout()
  }
}

function ssoUserData(
  sku, action, authSetting, submissionToken = null, source = null, rating = null, questionId = null
) {
  const ssoContext = {} as SsoContext
  ssoContext.action = action
  ssoContext.authSetting = authSetting
  ssoContext.sku = sku
  ssoContext.submissionToken = submissionToken
  if (typeof source === 'number') {
    ssoContext.source = source
  }
  if (typeof rating === 'number') {
    ssoContext.rating = rating
  }
  if (typeof questionId === 'number') {
    ssoContext.questionId = questionId
  }
  const encodedSsoContext = encodeSsoContext(ssoContext) as string
  turnToConfig.sso.userDataFn(encodedSsoContext)
}

function parseSsoContext(ssoContext: string): SsoContext {
  let data = {} as SsoContext
  if (ssoContext) {
    // Base64 encoded JSON data (submissionToken, action)
    data = JSON.parse(atob(ssoContext)) as SsoContext
  }
  return data
}

// for some reason when moved to webpack4, no longer could export individually to work in tests.
// it works fine when running the app, just not in tests.  Not ideal, but the whole file is being
// included in the build anyway.
export default {
  processSsoRegDone,
  ssoCreateReview,
  ssoCreateQuestion,
  ssoCreateAnswer,
  ssoCreateReply,
  ssoGetLoggedInData,
  ssoGetLoggedInDataForReview,
  processLoggedInDataDone,
  logout
}
